/**
 * InternationalPhoneInput
 *
 * A styled wrapper around react-phone-number-input that matches the existing
 * auth form design. Provides:
 *  - Country selector restricted to supported countries (via `countries` prop)
 *  - Automatic international dialing code
 *  - Country-aware validation
 *  - E.164 formatting
 *  - ISO country code extraction
 */
import PhoneInput from 'react-phone-number-input'
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js'
import 'react-phone-number-input/style.css'
import { BRAND } from '../../config'
import { SUPPORTED_COUNTRY_ISO_CODES, SUPPORTED_COUNTRIES, isSupportedCountry } from '../../config/supportedCountries'
import { CountrySelectMenu } from './CountrySelectMenu'

interface Props {
  value: string
  onChange: (value: string) => void
  onCountryChange?: (countryCode: string, countryName: string) => void
  placeholder?: string
  autoFocus?: boolean
  required?: boolean
  disabled?: boolean
}

export function InternationalPhoneInput({
  value,
  onChange,
  onCountryChange,
  placeholder = '+1 555 000 0000',
  autoFocus = false,
  required = false,
  disabled = false,
}: Props) {
  const handleChange = (newValue: string | undefined) => {
    const phoneValue = newValue || ''
    onChange(phoneValue)

    // Extract country code and name when phone changes
    if (phoneValue && onCountryChange) {
      try {
        const parsed = parsePhoneNumber(phoneValue)
        if (parsed) {
          onCountryChange(parsed.country || '', getCountryName(parsed.country || ''))
        }
      } catch {
        // Invalid phone, don't update country
      }
    }
  }

  return (
    <div className="international-phone-input-wrapper">
      <PhoneInput
        international
        addInternationalOption={false}
        defaultCountry={SUPPORTED_COUNTRIES[0].iso}
        countries={SUPPORTED_COUNTRY_ISO_CODES}
        countryOptionsOrder={SUPPORTED_COUNTRY_ISO_CODES}
        countrySelectComponent={CountrySelectMenu}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        autoFocus={autoFocus}
        required={required}
        disabled={disabled}
        className="international-phone-input"
      />
      <style>{`
        .international-phone-input-wrapper {
          position: relative;
        }

        .international-phone-input {
          width: 100%;
        }

        .international-phone-input .PhoneInputInput {
          width: 100%;
          border: 1px solid #e5e7eb;
          border-radius: 0.75rem;
          padding: 0.625rem 1rem;
          font-size: 0.875rem;
          color: #111827;
          outline: none;
          transition: all 0.2s;
        }

        .international-phone-input .PhoneInputInput::placeholder {
          color: #9ca3af;
        }

        .international-phone-input .PhoneInputInput:focus {
          ring: 2px;
          --tw-ring-color: ${BRAND.red};
          box-shadow: 0 0 0 2px ${BRAND.red}40;
        }

        .international-phone-input .PhoneInputInput {
          padding-left: 3.5rem;
        }

        /* ── Custom country select (CountrySelectMenu) ─────────────────────── */

        .international-phone-input .PhoneInputCountry-menu-container {
          position: absolute;
          left: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          z-index: 20;
        }

        .international-phone-input .country-select-trigger {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
        }

        .international-phone-input .country-select-trigger:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }

        .international-phone-input .country-select-trigger .PhoneInputCountryIcon {
          width: 1.25rem;
          height: 1rem;
          box-shadow: 0 0 0 1px rgba(0,0,0,0.1);
        }

        .international-phone-input .country-select-trigger-arrow {
          width: 0.75rem;
          height: 0.75rem;
          color: #6b7280;
          opacity: 0.7;
        }

        /*
         * The menu itself is rendered via a React portal into document.body
         * (see CountrySelectMenu) so it can escape any ancestor with
         * overflow: hidden (e.g. the phone-verification modal card). Its
         * position (top/left) is set inline via JS, not here. Because it's
         * portaled, these rules are intentionally NOT scoped under
         * .international-phone-input — that class no longer wraps this
         * element in the DOM once it's open.
         */
        .country-select-menu {
          min-width: 12rem;
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 0.75rem;
          box-shadow: 0 10px 25px -5px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05);
          padding: 0.375rem;
          overflow: hidden;
          z-index: 9999;
        }

        .country-select-option {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          width: 100%;
          height: 2.5rem;
          padding: 0 0.625rem;
          border: none;
          background: none;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          color: #111827;
          text-align: left;
          cursor: pointer;
          transition: background-color 0.15s;
        }

        .country-select-option:hover {
          background-color: #f9fafb;
        }

        .country-select-option--selected {
          background-color: ${BRAND.red}14;
          font-weight: 600;
        }

        .country-select-option .PhoneInputCountryIcon {
          width: 1.25rem;
          height: 1rem;
          flex-shrink: 0;
          box-shadow: 0 0 0 1px rgba(0,0,0,0.1);
        }

        .country-select-option-name {
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .country-select-option-dialcode {
          color: #9ca3af;
          font-size: 0.75rem;
          font-variant-numeric: tabular-nums;
        }
      `}</style>
    </div>
  )
}

/**
 * Validate if the phone number is valid for its country.
 */
export function validatePhone(phone: string): boolean {
  if (!phone) return false
  try {
    return isValidPhoneNumber(phone)
  } catch {
    return false
  }
}

/**
 * Check whether the phone number's detected country is one of the countries
 * this app currently supports (US, JO, KW). This is the client-side companion
 * to the backend's authoritative country_code check — it exists purely for UX
 * (fail fast, keep the user on the same step) and must never be relied upon as
 * the source of truth.
 */
export function isSupportedPhoneCountry(phone: string): boolean {
  try {
    const parsed = parsePhoneNumber(phone)
    return !!parsed?.country && isSupportedCountry(parsed.country)
  } catch {
    return false
  }
}

/**
 * Extract country code (ISO) from E.164 phone number.
 */
export function getPhoneCountryCode(phone: string): string {
  try {
    const parsed = parsePhoneNumber(phone)
    return parsed?.country || ''
  } catch {
    return ''
  }
}

/**
 * Get country name from ISO country code.
 * Uses the supported countries configuration.
 */
export function getCountryName(countryCode: string): string {
  const country = SUPPORTED_COUNTRIES.find((c) => c.iso === countryCode)
  return country?.name || countryCode
}
